import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerFinancialAccountsRoute, leagueCustomerFinancialPaymentVoidLinkRoute } from "network/api-routes";
import PlayerAccountLedger, { isPayableInvoice } from "./PlayerAccountLedger";
import ApplyTemplateWizard from "./ApplyTemplateWizard";
import PlayerDebtWizard from "./PlayerDebtWizard";
import { manualDebtExcludedTypes } from "./apply-template-policy";
import PlayerPaymentWizard from "./PlayerPaymentWizard";
import VoidInvoiceDialog from "./VoidInvoiceDialog";
import AlertDialog from "components/common/AlertDialog";
import {
  INVOICE_STATUS_OPTIONS,
  buildInvoiceQuery,
  defaultFinanceFilters,
  financeYearOptions,
} from "./account-finance-filters";

const statusLabelId = (status) =>
  status === "all"
    ? "gestion.accountFinance.filters.allStatuses"
    : `gestion.playerFinance.status.${status === "pending" ? "open" : status}`;

// `currency` is the page's resolved currency (balance first); some accounts don't carry one.
const AccountFinanceSection = ({
  customerId,
  account,
  availableBalance = null,
  currency: currencyProp,
  routes,
  startYear,
  onUpdated,
}) => {
  const currency = currencyProp || account?.currency || "";
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const supportsMembership = Boolean(account?.supportsMembership);
  const [filters, setFilters] = useState(() => defaultFinanceFilters());
  const [invoices, setInvoices] = useState([]);
  const [invoiceCache, setInvoiceCache] = useState({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [voidingInvoices, setVoidingInvoices] = useState([]);
  const [voidingLink, setVoidingLink] = useState(null);
  const [hasCollectionProcessor, setHasCollectionProcessor] = useState(false);
  const requestId = useRef(0);
  const yearOptions = useMemo(() => financeYearOptions(startYear), [startYear]);

  useEffect(() => {
    setFilters(defaultFinanceFilters());
    setPage(0);
    setSelectedIds([]);
    setInvoiceCache({});
  }, [routes?.invoices, supportsMembership]);

  useEffect(() => {
    if (!customerId || !user) return;
    performRequest("GET", leagueCustomerFinancialAccountsRoute(customerId), {}, true, user)
      .then((response) => setHasCollectionProcessor(Boolean(response.data?.gestion?.account?.has_collection_processor)))
      .catch(() => setHasCollectionProcessor(false));
  }, [customerId, user]);

  const rememberInvoices = useCallback((pageInvoices) => {
    setInvoiceCache((current) => {
      const next = { ...current };
      pageInvoices.forEach((invoice) => {
        next[invoice.id] = invoice;
      });
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!routes?.invoices || !user) return;
    const token = requestId.current + 1;
    requestId.current = token;
    setLoading(true);
    setError(null);
    try {
      const response = await performRequest(
        "GET",
        routes.invoices,
        buildInvoiceQuery(filters, pageSize),
        true,
        user,
        page + 1,
      );
      if (requestId.current !== token) return;
      const payload = response.data || {};
      const pageInvoices = Array.isArray(payload.data) ? payload.data : [];
      setInvoices(pageInvoices);
      setTotalPages(Number(payload.last_page || 1));
      setTotalResults(Number(payload.total || pageInvoices.length));
      rememberInvoices(pageInvoices);
    } catch (requestError) {
      if (requestId.current !== token) return;
      setError(requestError?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }));
    } finally {
      if (requestId.current === token) setLoading(false);
    }
  }, [filters, intl, page, pageSize, rememberInvoices, routes, user]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(0);
  };

  const selectedInvoices = useMemo(
    () => selectedIds.map((id) => invoiceCache[id]).filter(Boolean),
    [invoiceCache, selectedIds],
  );
  const selectedVoidable = useMemo(() => selectedInvoices.filter(isPayableInvoice), [selectedInvoices]);

  const selectDueMembership = async () => {
    const collected = [];
    let nextPage = 1;
    let lastPage = 1;
    do {
      const response = await performRequest(
        "GET",
        routes.invoices,
        { status: "pending", membership: 1, include_future: 0, per_page: 100 },
        true,
        user,
        nextPage,
      );
      const payload = response.data || {};
      collected.push(...(Array.isArray(payload.data) ? payload.data : []));
      lastPage = Number(payload.last_page || 1);
      nextPage += 1;
    } while (nextPage <= lastPage);
    rememberInvoices(collected);
    const dueIds = collected
      .filter((invoice) => isPayableInvoice(invoice) && invoice.balance_available !== false)
      .map((invoice) => invoice.id);
    setSelectedIds((current) => Array.from(new Set([...current, ...dueIds])));
  };

  const postManualPayment = async ({ custom, invoiceIds, amount, description, method, proof }) => {
    const formData = new FormData();
    if (custom) {
      formData.append("amount", amount);
      formData.append("description", description);
    } else {
      invoiceIds.forEach((id) => formData.append("invoice_ids[]", id));
    }
    formData.append("method", method);
    formData.append("proof", proof);
    await performRequest("POST", custom ? routes.addBalance : routes.invoicePayments, formData, true, user);
  };

  const refreshAfterSuccess = () => {
    setSelectedIds([]);
    setInvoiceCache({});
    load();
    onUpdated?.();
  };

  const copyPendingLink = async (invoice) => {
    try {
      await navigator.clipboard.writeText(invoice.payment_url);
      enqueueSnackbar(intl.formatMessage({ id: "gestion.playerFinance.linkCopied" }), { variant: "success" });
    } catch {
      enqueueSnackbar(intl.formatMessage({ id: "gestion.playerFinance.linkCopyFailed" }), { variant: "error" });
    }
  };

  const confirmVoidLink = async () => {
    const invoice = voidingLink;
    setVoidingLink(null);
    if (!invoice?.payment_id) return;
    try {
      await performRequest(
        "POST",
        leagueCustomerFinancialPaymentVoidLinkRoute(customerId, invoice.payment_id),
        {},
        true,
        user,
      );
      enqueueSnackbar(intl.formatMessage({ id: "gestion.playerFinance.linkVoided" }), { variant: "success" });
    } catch (requestError) {
      enqueueSnackbar(requestError?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    } finally {
      refreshAfterSuccess(); // the link may have been paid or expired meanwhile: reload either way
    }
  };

  const resetFilters = () => {
    setFilters(defaultFinanceFilters());
    setPage(0);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            spacing={1}
          >
            <RevaText variant="h6">{intl.formatMessage({ id: "gestion.accountFinance.movements" })}</RevaText>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {supportsMembership && (
                <Button variant="outlined" onClick={selectDueMembership} disabled={loading}>
                  {intl.formatMessage({ id: "gestion.playerFinance.selectUpToDate" })}
                </Button>
              )}
              <Button variant="contained" onClick={() => setWizard("payment")} disabled={!routes}>
                {intl.formatMessage({ id: "gestion.playerFinance.registerPayment" })}
              </Button>
              <Button variant="outlined" onClick={() => setWizard("apply")} disabled={!routes}>
                {intl.formatMessage({ id: "gestion.applyPrice.button" })}
              </Button>
              <Button variant="outlined" onClick={() => setWizard("debt")} disabled={!routes}>
                {intl.formatMessage({ id: "gestion.playerFinance.generateCharge" })}
              </Button>
              {routes?.voidInvoices && selectedVoidable.length > 0 && (
                <Button variant="outlined" color="error" onClick={() => setVoidingInvoices(selectedVoidable)}>
                  {intl.formatMessage({ id: "gestion.playerFinance.voidSelected" }, { count: selectedVoidable.length })}
                </Button>
              )}
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              select
              size="small"
              label={intl.formatMessage({ id: "gestion.accountFinance.filters.status" })}
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
              sx={{ minWidth: 160 }}
            >
              {INVOICE_STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {intl.formatMessage({ id: statusLabelId(status) })}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={intl.formatMessage({ id: "gestion.accountFinance.filters.year" })}
              value={filters.year}
              onChange={(event) => updateFilter("year", event.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{intl.formatMessage({ id: "gestion.accountFinance.filters.allYears" })}</MenuItem>
              {yearOptions.map((year) => (
                <MenuItem key={year} value={String(year)}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              size="small"
              label={intl.formatMessage({ id: "gestion.accountFinance.filters.from" })}
              value={filters.from}
              onChange={(event) => updateFilter("from", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              size="small"
              label={intl.formatMessage({ id: "gestion.accountFinance.filters.to" })}
              value={filters.to}
              onChange={(event) => updateFilter("to", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {supportsMembership && (
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.membershipOnly}
                    onChange={(event) => updateFilter("membershipOnly", event.target.checked)}
                  />
                }
                label={intl.formatMessage({ id: "gestion.accountFinance.filters.membershipOnly" })}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={filters.includeFuture}
                  onChange={(event) => updateFilter("includeFuture", event.target.checked)}
                />
              }
              label={intl.formatMessage({ id: "gestion.playerFinance.showFutureMonths" })}
            />
            <Button size="small" onClick={resetFilters}>
              {intl.formatMessage({ id: "gestion.accountFinance.filters.reset" })}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {loading && invoices.length === 0 ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
              <PlayerAccountLedger
                invoices={invoices}
                membershipOnly={filters.membershipOnly}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                currency={currency}
                onVoid={routes?.voidInvoices ? (invoice) => setVoidingInvoices([invoice]) : null}
                onCopyLink={navigator.clipboard?.writeText ? copyPendingLink : null}
                onVoidLink={(invoice) => setVoidingLink(invoice)}
                pagination={{
                  page,
                  totalPages,
                  pageSize,
                  totalResults,
                  onPageChange: setPage,
                  onPageSizeChange: (size) => {
                    setPageSize(size);
                    setPage(0);
                  },
                  disabled: loading,
                }}
              />
            </Box>
          )}
        </Stack>
      </CardContent>

      <Dialog open={Boolean(wizard)} onClose={() => setWizard(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {intl.formatMessage({
            id:
              {
                payment: "gestion.playerFinance.registerPayment",
                debt: "gestion.playerFinance.generateCharge",
                apply: "gestion.applyPrice.title",
              }[wizard] ?? "gestion.playerFinance.registerPayment",
          })}
        </DialogTitle>
        <DialogContent dividers>
          {wizard === "apply" && (
            <ApplyTemplateWizard
              customerId={customerId}
              account={account}
              currency={currency}
              onApplied={refreshAfterSuccess}
              onBack={() => setWizard(null)}
            />
          )}
          {wizard === "payment" && (
            <PlayerPaymentWizard
              player={{ phone_number: account?.phoneNumber }}
              selectedInvoices={selectedInvoices}
              availableBalance={availableBalance}
              currency={currency}
              hasCollectionProcessor={hasCollectionProcessor}
              onRegisterManual={postManualPayment}
              onGenerateLink={async (invoiceIds) => {
                const response = await performRequest(
                  "POST",
                  routes.paymentLinks,
                  { invoice_ids: invoiceIds },
                  true,
                  user,
                );
                return response.data;
              }}
              onVoidLink={(paymentId) =>
                performRequest(
                  "POST",
                  leagueCustomerFinancialPaymentVoidLinkRoute(customerId, paymentId),
                  {},
                  true,
                  user,
                )
              }
              onRefresh={async () => {
                await load();
                onUpdated?.();
              }}
              onSuccess={refreshAfterSuccess}
              onBack={() => setWizard(null)}
            />
          )}
          {wizard === "debt" && (
            <PlayerDebtWizard
              currency={currency}
              excludedTypes={manualDebtExcludedTypes(account)}
              onCreate={(payload) => performRequest("POST", routes.debts, payload, true, user)}
              onSuccess={refreshAfterSuccess}
              onBack={() => setWizard(null)}
            />
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog
        hideButton
        isOpen={Boolean(voidingLink)}
        alertTitle={intl.formatMessage({ id: "gestion.playerFinance.voidLinkConfirmTitle" })}
        alertBody={intl.formatMessage({ id: "gestion.playerFinance.voidLinkConfirmBody" })}
        confirmButtonTitle={intl.formatMessage({ id: "gestion.playerFinance.voidLink" })}
        onConfirm={confirmVoidLink}
        onCancel={() => setVoidingLink(null)}
      />
      {routes?.voidInvoices && (
        <VoidInvoiceDialog
          invoices={voidingInvoices}
          currency={currency}
          voidRoute={routes.voidInvoices}
          onClose={() => setVoidingInvoices([])}
          onVoided={() => {
            setVoidingInvoices([]);
            refreshAfterSuccess();
          }}
        />
      )}
    </Card>
  );
};

export default AccountFinanceSection;
